import { TVideoType } from '../common/videoUtils';
import { TKeystoneAffix } from './keystoneAffixTypes';
import { TFormattedAffixData } from './keystoneAffixTypes';
import { IBracketReplayRestrictionSummary, IBracketSpecRestrictionSummary, IEventImage } from './eventTypes';
import { TRegionResource, TSubRegionResource } from './utilityTypes';
import { TShortStorageItemSummary } from './storageTypes';
import { TDungeonSummary, TInstanceSummary } from './raidTypes';
import { ISharedUserInstance } from '../backend/models/sharedUserModel';

export interface IEventBracketSummary {
  id: number;
  title: string;
  eventId: number;
  slug: string;
  format: BracketFormat;
  visibility: BracketVisibility;
  deathPenaltySeconds: number;
  autoSelfScoreEnabled: boolean;
  showSeeds: boolean;
  backgroundImage: IEventImage;
  timelineBracketStartIcon: string | null;
  timelineBracketEndIcon: string | null;
  bracketStartsDate: string | null;
  bracketEndsDate: string | null;
  gracePeriod: number;
  checkinStartsDate: string | null;
  checkinEndsDate: string | null;
  requireTeamCheckin: boolean;

  rulesetSeasonSlug: string | null;
  rulesetType: string | null;
  rulesetRealmType: string;
  rulesetRestrictsSpecs: boolean;
  rulesetRestrictsReplays: boolean;
  rulesetScoringAlgorithm: string;
  rulesetTimedRunsOnly: boolean;
  rulesetLiveTrackedOnly: boolean;
  rulesetMinRequiredRuns: number;
  rulesetQualifyByStartTime: boolean;
  rulesetMaxScoredDungeons: number | null;
  rulesetMinKeystoneLevel: number | null;
  rulesetMaxKeystoneLevel: number | null;
  requiresRegistration: boolean;
}

export interface IEventBracketDungeonSummary {
  id: number;
  affixes: TFormattedAffixData[];
  minMythicLevel: number | null;
  maxMythicLevel: number | null;
  showDungeonAt: string | null;
  showAffixesAt: string | null;
  dungeon?: TInstanceSummary | null;
}

export enum EventBracketUserRestrictionType {
  ALLOW = 'allow',
  DENY = 'deny'
}

export interface IEventBracketUserRestrictionSummary {
  id: number;
  type: EventBracketUserRestrictionType;
  user?: Pick<ISharedUserInstance, 'id' | 'name' | 'avatar' | 'profile_privacy'> | null;
}

export interface IEventBracketDetails {
  id: number;
  title: string;
  eventId: number;
  slug: string;
  visibility: BracketVisibility;
  format: BracketFormat;
  rounds: number;
  startAtRoundCount: number;
  stopAtRoundCount: number;
  showSeeds: boolean;
  autoSelfScoreEnabled: boolean;
  deathPenaltySeconds: number;
  backgroundImage: IEventImage;
  timelineBracketStartIcon: string | null;
  timelineBracketEndIcon: string | null;
  bracketTooltip: string | null;
  suggestedDungeons: IEventBracketDungeonSummary[];
  specRestrictions: IBracketSpecRestrictionSummary[];
  replayRestrictions: IBracketReplayRestrictionSummary[];
  thirdPlaceWinner: boolean;
  numTieBreakerMatches: number;
  requireTeamCheckin: boolean;
  supportsPoints: boolean;
  teamsCount: number | null;

  rulesetSeasonSlug: string | null;
  rulesetType: string | null;
  rulesetRealmType: string;
  rulesetRestrictsSpecs: boolean;
  rulesetRestrictsReplays: boolean;
  rulesetScoringAlgorithm: string;
  rulesetTimedRunsOnly: boolean;
  rulesetLiveTrackedOnly: boolean;
  rulesetMinRequiredRuns: number;
  rulesetQualifyByStartTime: boolean;
  rulesetMaxScoredDungeons: number | null;
  rulesetMinKeystoneLevel: number | null;
  rulesetMaxKeystoneLevel: number | null;
  requiresRegistration: boolean;

  publishedDate: string | null;
  gracePeriod: number;
  bracketStartsDate: string | null;
  bracketEndsDate: string | null;
  usStartsAt: string | null;
  usEndsAt: string | null;
  euStartsAt: string | null;
  euEndsAt: string | null;
  krStartsAt: string | null;
  krEndsAt: string | null;
  twStartsAt: string | null;
  twEndsAt: string | null;
  cnStartsAt: string | null;
  cnEndsAt: string | null;
  checkinStartsDate: string | null;
  checkinEndsDate: string | null;

  // TODO: add type;
  userRestrictions: any[];
}

export interface IEventBracketMatchDetails {
  id: number;
  backgroundColor: string | null;
  borderColor: string | null;
  firstTeamPlaceholder: string | null;
  firstTeamStatus: TeamMatchStatus;
  secondTeamPlaceholder: string | null;
  secondTeamStatus: TeamMatchStatus;
  winnerTeamId: number | null;
  bonusRounds: number;
  round: number;
  position: BracketPosition;
  match: number;
  status: MatchStatus;
  gracePeriodEndsAt: string;
  updatedAt: string;
  secondTeam: TBracketTeamSummary | null;
  firstTeam: TBracketTeamSummary | null;
  winnerTeam: TBracketTeamSummary | null;
  games?: IEventBracketMatchGameDetails[];
  scores?: IEventBracketMatchScoreSummary[];
}

export interface IVideoDetails {
  videoId: string;
  videoType: TVideoType;
  videoTimestamp: number;
}

export interface IEventBracketMatchGameDetails extends IVideoDetails {
  id: number;
  status: MatchGameStatus;
  matchId: number;
  gameOrder: number;
  gameType: GameType;
  splitsType: SplitsType;
  dungeon: TInstanceSummary | null;
  affixes: TKeystoneAffix[];
  winnerTeamId: number | null;
  bracketDungeonId: number | null;
  description: string | null;
  descriptionBBCode: string | null;
  winnerTeam: TBracketTeamSummary | null;
  syncRosterType: SyncRosterType;
  syncRosterValue: string | null;
  firstTeamRoster: IForceRosterMember[] | null;
  secondTeamRoster: IForceRosterMember[] | null;
  details: IEventBracketMatchGamePVEDetails | IEventBracketMatchGamePVPDetails | null;
  updatedAt: string;
}

export type MatchScoreResult = {
  [key: number]: number;
};

// this is what gets saved to the db
export type TPersistentMatchScoreResult = {
  tally: string;
  matchResults: MatchScoreResult;
};

export interface IEventBracketMatchScoreSummary {
  id: number;
  matchId: number;
  creatorUserId: number;
  creatorTeamId: number;
  reportedAt: string;
  results: MatchScoreResult[];
}

export interface IEventBracketMatchGamePVPDetails {
  firstTeamSplit1: string | null;
  secondTeamSplit1: string | null;
  dampening: number | null;
  duration: string | null;
}

export interface IForceRosterMember {
  name: string;
  class: string;
  spec: string;
  role: string | null;
  raceId: number | null;
  genderId: number | null;
  covenant?: {
    id: number;
    name: string;
  };
  isSubstitute: boolean;
}

export interface IEventBracketMatchGamePVEDetails {
  firstTeamBan: TDungeonSummary | null;
  secondTeamBan: TDungeonSummary | null;
  firstTeamDeaths: number;
  secondTeamDeaths: number;
  mythicLevel: number | null;
  firstTeamSplit1: string | null;
  secondTeamSplit1: string | null;
  firstTeamSplit2: string | null;
  secondTeamSplit2: string | null;
  firstTeamSplit3: string | null;
  secondTeamSplit3: string | null;
  firstTeamSplit4: string | null;
  secondTeamSplit4: string | null;
  firstTeamSplit5: string | null;
  secondTeamSplit5: string | null;
}

export interface IEventBracketTeamDetails {
  id: number;
  type: number;
  name: string;
  region: TRegionResource | null;
  subRegion: TSubRegionResource | null;
  teamId: number;
  seed: number | null;
  checkedInAt: string | null;
  checkedInByUser: any;
  checkedOutAt: string | null;
  points: number | null;
  checkedOutByUser: any;
  teamSlug: string;
  teamEventProfileUrl: string;
  owner: any;
  faction: string;
  members: any[];
  bracketLogoId: number | null;
  bracketLogoUrl: string | null;
  bracketLogo: TShortStorageItemSummary | null;
  noteIcon: string | null;
  note: string | null;
}

export interface IEventBracketTeamShortDetails {
  id: number;
  type: number;
  name: string;
  regionId: number | null;
  teamId: number;
  teamSlug: string;
  teamEventProfileUrl: string;
  iconLogoUrl: string | null;
  iconLogo: TShortStorageItemSummary | null;
  owner: any;
  faction: string;
}

export enum EventBracketRounds {
  BEST_OF_ONE = 1,
  BEST_OF_THREE = 3,
  BEST_OF_FIVE = 5,
  BEST_OF_SEVEN = 7
}

export enum RemoveEventBracketTeamMode {
  UNSEEDED = 'unseeded',
  ALL = 'all',
  UNCHECKED_IN = 'unchecked-in'
}

export enum BracketPosition {
  UPPER = 'upper',
  LOWER = 'lower',
  THIRD_PLACE = 'third_place' // also used for tie breakers
}

// TODO: check if this is still used
export enum MatchStatus {
  UNSTARTED = 'unstarted',
  SKIP = 'skip',
  IN_PROGRESS = 'in_progress',
  REQUIRE_ADMIN = 'require_admin',
  COMPLETE = 'complete'
}

export enum MatchGameStatus {
  SKIP = 'skip',
  UNSTARTED = 'unstarted',
  IN_PROGRESS = 'in_progress',
  COMPLETE = 'complete',
  TIE = 'tie'
}

export enum TeamMatchStatus {
  ACTIVE = 'active',
  NO_SHOW = 'no_show',
  BYE = 'bye'
}

export const enum GameType {
  PVP = 'pvp',
  PVE = 'pve'
}

export enum SplitsType {
  RELATIVE = 'relative',
  NOT_RELATIVE = 'not_relative'
}

export enum BracketFormat {
  STAGE = 'stage',
  SINGLE_ELIMINATION = 'single_elimination',
  DOUBLE_ELIMINATION = 'double_elimination',
  TIME_TRIALS = 'time_trials',
  THE_GREAT_PUSH = 'the_great_push',
  GAUNTLET = 'gauntlet',
  PLUNDERSTORM_ROYALE = 'plunderstorm_royale',
  LIVE_FORMAT = 'live_format'
}

export enum BracketRulesetType {
  TIME_TRIALS = 'time_trials',
  BREAK_THE_META = 'break_the_meta',
  THE_GREAT_PUSH = 'great_push',
  LIVE_FORMAT = 'live_format'
}

export enum BracketVisibility {
  FULL_VISIBILITY = 'full-visibility',
  NO_VISIBILITY = 'no-visibility',
  TIMELINE_ONLY = 'timeline-only',
  BRACKET_ONLY = 'bracket-only'
}

export enum SyncRosterType {
  WISAK = 'wisak',
  LIVE_TRACKING = 'livetracking',
  MANUAL = 'manual'
}

export enum BracketRulesetScoringAlgorithm {
  STANDARD = 'standard',
  BLIZZARD = 'blizzard',
  DUNGEON_DOJO = 'dungeon_dojo',
  DUNGEON_DOJO_v2 = 'dungeon_dojo_v2',
  GREAT_PUSH = 'great_push',
  GREAT_PUSH_ABSOLUTE = 'great_push_absolute',
  GREAT_PUSH_S2 = 'great_push_season_2',
  GREAT_PUSH_S2_ABSOLUTE = 'great_push_season_2_absolute',
  KEYSTONE_BUILDERS = 'keystone_builders',
  KEYSTONE_BUILDERS_QUALIFIERS = 'keystone_builders_qualifiers',
  LAST_STAND = 'last_stand_2021',
  TIME_REVERSE = 'time_reverse',
  MDI_REPLAY_RACE_FASTEST = 'mdi_replay_race',
  MDI_REPLAY_RACE_HIGHEST = 'mdi_replay_race_highest'
}

export interface IPublishedBracketSummary {
  id: number;
  title: string;
  slug: string;
  format: BracketFormat;
  requireTeamCheckin: boolean;
  checkinStartsDate: string | null;
  checkinEndsDate: string | null;
  autoSelfScoreEnabled: boolean;
  showSeeds: boolean;
  deathPenaltySeconds: number;
  thirdPlaceMatch: IEventBracketMatchDetails | null;
  tieBreakerMatches: IEventBracketMatchDetails[];
  bracketTooltipHtml: string | null;
  bracketStartsDate: string | null;
  bracketEndsDate: string | null;
  backgroundImage: IEventImage;
  segments: {
    [BracketPosition.UPPER]: IPublishedBracketSummarySegment;
    [BracketPosition.LOWER]: IPublishedBracketSummarySegment | null;
  };
  startAtRoundCount: number;
  stopAtRoundCount: number;
  roundsInBracket: Record<BracketPosition.UPPER | BracketPosition.LOWER, number>;
  dungeons: IEventBracketDungeonSummary[];
  rulesetSeasonSlug: string | null;
  rulesetType: string | null;
  rulesetRealmType: string;
  rulesetRestrictsSpecs: boolean;
  rulesetRestrictsReplays: boolean;
  rulesetScoringAlgorithm: string;
  rulesetTimedRunsOnly: boolean;
  rulesetLiveTrackedOnly: boolean;
  rulesetMinRequiredRuns: number;
  rulesetQualifyByStartTime: boolean;
  rulesetMaxScoredDungeons: number | null;
  rulesetMinKeystoneLevel: number | null;
  rulesetMaxKeystoneLevel: number | null;
  requiresRegistration: boolean;
  gracePeriod: number;
}

export interface IUserRegistrationBracket {
  id: number;
  slug: string;
  title: string;
  bracketTeamId: number;
  checkinStartsDate: string | null;
  checkinEndsDate: string | null;
  isScoringStarted: boolean;
  isCheckinRequired: boolean;
  isCheckinAccepted: boolean;
  isAutoSelfScoreEnabled: boolean;
  bracketStartsDate: string | null;
  bracketEndsDate: string | null;
  matches: IEventBracketMatchDetails[];
}

export interface IPublishedBracketSummarySegment {
  rounds: IPublishedBracketSummaryRounds;
}

export interface IPublishedBracketSummaryRounds {
  [round: number]: IEventBracketMatchDetails[];
}

export type TBracketTeamSummary = {
  id: number;
  seed: number | null;
  teamEventProfileUrl: string;
  charter_id: number;
  group_id: number;
  platoon_id: number;
  status: string;
  name: string;
  slug: string;
  region: TRegionResource;
  subRegion: TRegionResource | null;
  namespace: string;
  eventsData: TEventTeamSummary | null;
  faction: string;
  bracketLogoUrl: string | null;
  bracketLogo: TShortStorageItemSummary | null;
  icon_logo_url: string | null;
  icon_logo: TShortStorageItemSummary | null;
  group_type: number;
  isMythicPlusTeam: boolean;
  streamers?: any;
};
export type TEventTeamSummary = {
  id: number;
  name: string;
  backgroundImage: string | null;
  namespace: string;
  eventSlug: string;
};
export type TGetBracketTeamListFilters = {
  region?: string;
  search?: string;
};

export type TParsedRosterMember = {
  name: string;
  // name of the class
  class: string;
  // name of the spec
  spec: string;
  raceId: number | null;
  genderId: number | null;
  isSubstitute: boolean;
};
